import React, { useEffect, useMemo, useRef } from 'react'
import { Chat as ChatUI, Message, User as ChatUser, ChatMessageBoxProps } from "@progress/kendo-react-conversational-ui";
import { Button } from "@progress/kendo-react-buttons";
import '../App.scss';
import { ChatApi } from '../redux/api/ChatApi';
import { useNavigate, useParams } from 'react-router-dom';
import { IChatMessage } from '../types/IChatMessage';
import { useReduxDispatch, useReduxSelector } from '../redux/hooks';
import { AddMessage, CloseChat, MarkAsRead, PostMessage, StaffJoined, TypingMessage, ChatStarted, LeaveChat } from '../redux/reducers/ChatSlice';
import { IChatHeaderProps, IChatProps } from '../types/IChatProps';
import ChatHeader from './ChatHeader';
import { PartialSpinny, SimpleSpinny } from './Common/Spinny';
import { TextArea, TextAreaHandle } from '@progress/kendo-react-inputs';
import ChatMenu from './ChatMenu';
import { ITicket } from '../types/ITicket';

export default function Chat(props: IChatProps) {
    const navigate = useNavigate();
    const { id } = useParams();
    const chatId = props.id || id;
    const dispatch = useReduxDispatch();
    const config = useReduxSelector(s => s.chat?.config);
    const [postTicket, { isLoading: loggingTicket }] = ChatApi.useCreateTicketMutation();
    const { data: chat } = ChatApi.useGetChatQuery(chatId, { skip: !chatId });
    const { data: chatMessages, isFetching: fetchingMessages } = ChatApi.useGetMessagesQuery(chatId, { skip: !chatId });
    const { data: userProfile } = ChatApi.useGetProfileQuery();
    ChatApi.useGetConfigQuery(); //Required for playing sounds in the middleware
    const [acceptChat, { isLoading: joiningChat }] = ChatApi.useAcceptChatMutation();
    const [closeChat] = ChatApi.useCloseChatMutation();
    const [error, setError] = React.useState<string>("");
    const [message, setMessage] = React.useState<string>("");
    const [user, setUser] = React.useState<ChatUser>();
    const [leaveChat, { isLoading: leavingChat }] = ChatApi.useLeaveChatMutation();
    const [cursor, setCursor] = React.useState(null);

    //Load user Profile
    useEffect(() => {
        if (userProfile && chat) {
            const displayName = chat.EndUserContact_Id == userProfile?.Id ? chat.End_User : chat.Staff_From_Name;
            const profileImage = displayName == "Support Person" ? null : userProfile.Profile_Image_Url; //No profile image in Anonymous mode
            setUser({ id: userProfile?.Id, name: displayName, avatarUrl: profileImage } as ChatUser)
        }
    }, [userProfile, chat])


    const addNewMessage = (event: { message: Message; }) => {
        console.log('addNewMessage')
        var newMessage = { Id: 0, Chat_Id: chat?.Id, Is_Event: false, ConversationId: chat?.ConversationId, Text: event.message.text as string, From: user?.name, Created: new Date().toISOString(), Contact_Id: user?.id, Profile_Image: user?.avatarUrl as string } as IChatMessage

        //Post to Server with Signal R
        dispatch(PostMessage(newMessage));
        //Add to local storage for instant view
        dispatch(AddMessage(newMessage));

    };

    const messageBox = useRef<TextAreaHandle>(null);

    const sendMessage = () => {
        console.log('sendMessage', message);
        let sendMessage = message//messageBox.current.value; //_message || message;
        if (sendMessage) {
            var newMessage = { Id: 0, Chat_Id: chat?.Id, Is_Event: false, ConversationId: chat?.ConversationId, Text: sendMessage as string, From: user?.name, Created: new Date().toISOString(), Contact_Id: user?.id, Profile_Image: user?.avatarUrl as string } as IChatMessage
            var newChat = !chat.Last_Message_Contact_Id;

            //Post to Server with Signal R
            dispatch(PostMessage(newMessage));
            //Add to local storage for instant view
            dispatch(AddMessage(newMessage));

            //Tell the signal R server that a new chat may have started so the other staff gets notified.
            console.log('ChatStarted? newChat=', newChat)
            newChat && dispatch(ChatStarted(chat));

            setMessage("");
        }
    };

    //The messages to render from storage - useMemo(() => 
    const elementRef = useRef<HTMLDivElement>();
    useEffect(() => elementRef.current?.scrollIntoView());
    var renderMessages = useMemo(() => {
        const renderItems = chatMessages && [...chatMessages];
        if (!chat?.StaffContact_Id && userProfile?.Features.SSIChat) { renderItems?.push({ Chat_Id: chat?.Id, Is_Event: true, Text: 'Waiting for Support Staff to join...', Created: new Date().toISOString(), Contact_Id: chat?.EndUserContact_Id, Profile_Image: chat?.EndUser_Profile_Path } as IChatMessage) }

        return renderItems?.filter(c => c.Chat_Id === chat?.Id)?.map((cm, index) => {
            return cm.Is_Event ? {
                //Event
                text: (index === chatMessages.length - 1 ? <div ref={elementRef} className="event" dangerouslySetInnerHTML={{ __html: cm.Text }}></div> : <span className="event" dangerouslySetInnerHTML={{ __html: cm.Text }}></span>),
                author: {
                    id: cm.Contact_Id,
                }
            }
                : {
                    text: (index === chatMessages.length - 1 ? <div ref={elementRef}>{cm.Text}</div> : cm.Text),
                    timestamp: new Date(cm.Created),
                    author: {
                        id: cm.Contact_Id,
                        name: cm.From,
                        avatarUrl: cm.Profile_Image
                    }
                } as Message
        }) as Message[]
    }, [chat, chatMessages, userProfile]);


    //Close the chat.
    const closeCurrentChat = async () => {
        if (!window.confirm('Are you sure you want to close this chat?'))
            return;

        await closeChat(chat).unwrap().then((res) => {
            dispatch(CloseChat(chat));
        });
    }

    //Staff accepts a chat request.
    const acceptRequest = async () => {
        console.log('acceptRequest');

        await acceptChat(chat).unwrap().then((res) => {

            console.log('before StaffJoined', res);

            //Tell chat Server we have joined the chat
            dispatch(StaffJoined(chat));

            console.log('dispatch StaffJoined');

        }).catch(ex => {
            console.error(ex);
            setError(ex['data']);
        });


    }

    const isRequest = !chat?.StaffContact_Id;
    const isClosed = chat?.IsClosed;
    const endUserLeft = chat?.EndUser_Left;
    const notPartOfConversation = chat?.EndUserContact_Id !== userProfile?.Id && chat?.StaffContact_Id !== userProfile?.Id;
    let headerProps: IChatHeaderProps = (chat?.EndUserContact_Id === userProfile?.Id
        ? { image: chat?.Staff_Profile_Path, header: chat?.EndUser_Header, subheader: chat?.EndUser_Sub_Header, uId: chat?.Staff_Uid }
        : { image: chat?.EndUser_Profile_Path, header: chat?.Staff_Header || chat?.End_User, subheader: chat?.Staff_Sub_Header, uId: chat?.EndUser_Uid }) as IChatHeaderProps
    headerProps = { ...headerProps, closeChat: closeCurrentChat, isClosed: chat?.IsClosed, showOptions: props.showOptions, conversationUid: chat?.ConversationId }

    //Set a timer to switch to Log ticket controls after a certain amount of time.
    const [showLogControls, setLogControls] = React.useState<boolean>(false);
    useEffect(() => {
        const timer = setInterval(() => {
            //console.log('LogControls', (new Date().getTime() - new Date(chat?.Created + 'Z').getTime()) / 1000.0, 'Show_Log_Option_After_Mins', config.Show_Log_Option_After_Mins, 'Created', chat?.Created);
            if (userProfile?.Features.SSIChat && isRequest && config.Show_Log_Option && chat?.Created && ((new Date().getTime() - new Date(chat?.Created + 'Z').getTime()) / 1000.0) > config.Show_Log_Option_After_Mins) {
                setLogControls(true);
                clearInterval(timer);
            } else {
                setLogControls(false);
            }
        }, 1000);

        return () => {
            clearInterval(timer);
        }
    }, [chat])

    const logTicket = async () => {
        const newTicket = {
            Ticket_Source_Id: 9,
            Company_Id: userProfile?.Company_Id,
            Company_Contact: userProfile?.Id,
            Company_Contact_Uid: userProfile?.Uid,
            Call_Desc: "Logged from chat: " + chatMessages?.filter(m => !m.Is_Event).map(m => m.Text).join('\r\n'),
        } as ITicket;

        //log a ticket via the API and show a success message
        try {
            var createdTicket = await postTicket(newTicket).unwrap() as ITicket;

            var eventMessage = `${user?.name} logged ${userProfile?.Phrases.CallPhrase} <a href='/calls/callhistory.aspx?call_id=${createdTicket.ID}'>#${createdTicket.ID}</a> from chat.`
            var newMessage = { Id: 0, Chat_Id: chat?.Id, Is_Event: true, ConversationId: chat?.ConversationId, Text: eventMessage, From: user?.name, Created: new Date().toISOString(), Contact_Id: user?.id, Profile_Image: user?.avatarUrl as string } as IChatMessage

            //Post to Server with Signal R
            dispatch(PostMessage(newMessage));

            //Leave the chat
            const result = await leaveChat(chat).unwrap().then((res) => {
                //Tell chat Server we have closed the chat
                dispatch(LeaveChat(chat));
            });

            navigate(`/deeplink?url=/Calls/CallLoggedSuccessfully.aspx?Call_Id=${createdTicket.ID}`, { replace: true });
        } catch (ex) {
            setError(`Error logging ${userProfile?.Phrases.CallPhrase} from Chat:` + ex['data']);
        }
    }

    useEffect(() => {
        const input = messageBox.current;
        if (input) input.element.current.setSelectionRange(cursor, cursor);
    }, [messageBox, cursor, message]);


    const customMessageBox = (props: ChatMessageBoxProps) => {
        if (error) return <div className='k-form-error'>{error}</div>

        //Show option to log a ticket after wainting for the configured amount of time.
        if (showLogControls) return <>
            <p>All our technitians are busy helping others, would you like to log a {userProfile?.Phrases.CallPhrase} instead?</p>
            <Button onClick={logTicket} className='chat-yes-button' themeColor={"primary"} size='large' icon='check'>Yes</Button>
            <Button onClick={() => setLogControls(false)} className='chat-accept-button m-1' themeColor={"secondary"} size='large' icon='close'>No</Button>
        </>;

        return (
            !isClosed && isRequest && !userProfile?.Features.SSIChat
                ?
                <>
                    {joiningChat
                        ? <SimpleSpinny />
                        : <Button onClick={acceptRequest} className='chat-accept-button' themeColor={"primary"} size='large' icon='check'>Accept Chat Request</Button>
                    }
                </>
                :
                <>
                    <TextArea disabled={isClosed || endUserLeft || notPartOfConversation} ref={messageBox} autoFocus style={{ maxHeight: 80 }} autoSize={true} rows={1}
                        onBeforeInput={() => dispatch(TypingMessage(chat))}
                        value={message}
                        onChange={(e) => {
                            setCursor(messageBox.current.element.current.selectionStart);
                            setMessage(e.value);
                        }}
                        onKeyPress={(e) => {
                            //console.log('onKeyPress', e.currentTarget)
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                e.stopPropagation();
                                sendMessage();
                            }
                        }}

                    />

                    {!isClosed && !endUserLeft && !notPartOfConversation &&
                        <Button className='send-button' title='Send message' fillMode='flat' onClick={() => sendMessage()}><svg version="1.1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 16 16"><path d="M0,14.3c-0.1,0.6,0.3,0.8,0.8,0.6l14.8-6.5c0.5-0.2,0.5-0.6,0-0.8L0.8,1.1C0.3,0.9-0.1,1.1,0,1.7l0.7,4.2C0.8,6.5,1.4,7,1.9,7.1l8.8,0.8c0.6,0.1,0.6,0.1,0,0.2L1.9,8.9C1.4,9,0.8,9.5,0.7,10.1L0,14.3z"></path></svg>
                        </Button>}
                    <ChatMenu chat={chat} profile={userProfile} />
                </>
        );
    };
    var customMessageBoxMem = useMemo(() => customMessageBox, [chat, user, message, showLogControls]);

    if (!chat) return <></>
    //const chatUidRef = useRef<ChatUI>();
    //chatUidRef.current?.forceUpdate();

    return (
        <div className='chat-wrapper'>
            {isClosed && <i className='chat-status'>This chat has been closed.</i>}
            {(loggingTicket || leavingChat) && <PartialSpinny />}
            {props.showHeader &&
                <ChatHeader {...headerProps} />}

            <ChatUI onToolbarActionExecute={(e) => console.log('action', e)} onActionExecute={(e) => console.log('action', e)}
                user={user as ChatUser}
                messages={renderMessages}
                onMessageSend={addNewMessage}
                width={400}
                showToolbar={false}
                messageBox={customMessageBoxMem}
            />
            {/* </Provider> */}
        </div>
    )

}

const AlwaysScrollToBottom = () => {
    const elementRef = useRef<HTMLDivElement>();
    useEffect(() => elementRef.current.scrollIntoView());
    return <div ref={elementRef}>bottom</div>;
};